import { useEffect, useId, useState } from "react";

import { useBackClose } from "../back.js";
import { useText } from "../i18n.js";
import { DIALOGS } from "../text/app.js";

/** What to ask a name for, and what to do with it. */
export interface NameRequest {
  readonly title: string;
  /** Under the title: what the name is for. */
  readonly note?: string;
  readonly initial?: string;
  readonly placeholder?: string;
  /** The button that takes the name: "Create", "Rename". */
  readonly action: string;
  readonly onName: (name: string) => void;
}

/** Long enough for any section a person would name; the list shows less. */
const MAX_NAME = 60;

/**
 * Asks for one name, in the app's own look rather than the browser's prompt:
 * a small dialog over the page with the field ready to type in. Enter takes
 * it, Escape, Back or a tap outside leaves it; an empty name cannot be taken.
 */
export function NameDialog({ request, closing, onClose }: { readonly request: NameRequest; readonly closing: boolean; readonly onClose: () => void }): React.JSX.Element {
  const t = useText(DIALOGS);
  const [value, setValue] = useState(request.initial ?? "");
  const titleId = useId();
  const name = value.trim();
  useBackClose(!closing, onClose);
  useEffect(() => {
    // Ahead of anything else listening, so Escape closes this and not what lies under it.
    const key = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      onClose();
    };
    window.addEventListener("keydown", key, true);
    return () => window.removeEventListener("keydown", key, true);
  }, [onClose]);
  return (
    <div className={`modal-backdrop${closing ? " closing" : ""}`} onClick={onClose}>
      <form
        className="modal ask-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(event) => event.stopPropagation()}
        onSubmit={(event) => {
          event.preventDefault();
          if (name.length === 0) return;
          onClose();
          request.onName(name);
        }}
      >
        <h2 id={titleId}>{request.title}</h2>
        {request.note != null ? <p className="ask-dialog-note">{request.note}</p> : null}
        <input
          autoFocus
          value={value}
          onChange={(event) => setValue(event.target.value)}
          onFocus={(event) => event.currentTarget.select()}
          placeholder={request.placeholder}
          aria-label={request.title}
          maxLength={MAX_NAME}
          enterKeyHint="done"
          autoComplete="off"
        />
        <div className="ask-dialog-actions">
          <button type="button" onClick={onClose}>
            {t.cancel}
          </button>
          <button type="submit" className="primary" disabled={name.length === 0}>
            {request.action}
          </button>
        </div>
      </form>
    </div>
  );
}
